/**
 * Mampe cart gift — free-gift comfort layer on top of native Shopify discounts.
 * Ported from Aseli-Store snippet-aseli-cart-gift.js (Dawn) to the Impact theme.
 *
 * Architecture (see docs/shopify-free-gift.md):
 *  - The gift code itself is a server-side BxGY discount ("buy >= threshold, get
 *    1x gift product free"). This script NEVER decides about money — it only
 *    adds/removes the gift line and sets the checkout discount session.
 *  - Which code triggers which gift comes from ACTIVE free_gift_action metaobjects,
 *    serialized into <script data-mampe-gift-catalog> by snippets/mampe-gift-catalog.liquid.
 *  - One field for ALL codes: unknown codes just get a /discount/<CODE> session
 *    ("wird im Checkout geprüft") — validity stays Shopify's call.
 *  - Time window: catalog entries may carry startsAt/endsAt (ISO-UTC). Outside the
 *    window the code is kept but inert (no session, no gift line) — prevents a
 *    full-price gift line before the campaign starts / after it ends.
 *  - Sequencing (bug fixes 2026-09-10): the code session (/discount/<CODE>) is AWAITED
 *    before any cart mutation/render — the BxGY line discount is computed per-request
 *    from that session, so rendering before the cookie settles showed the gift at
 *    full price; verifyGiftDiscount() re-renders once the allocation shows up.
 *    clearCode() is strictly sequential (remove line -> clear session -> ONE refresh)
 *    — parallel mutations broke the checkout session with Shopify's generic error.
 *    Removing the gift line DIRECTLY also clears the whole code (chip + session +
 *    cookie): an active code without its gift line dead-ends Shopify checkout with
 *    a generic error that sticks for the cart token (3rd round, same day).
 *  - Conflict guard (2026-09-16): Shopify flags the session code applicable:false
 *    when another product discount blocks the BxGY — all products in a BxGY's buy
 *    set are ineligible for further product discounts ("best value overall" wins,
 *    help.shopify.com discount-combinations), and our BxGY buys from the
 *    ALL-products collection, so e.g. the Konfirabatt on configurator products
 *    makes it yield. The guard then removes line + code via clearCode() and shows
 *    a "not combinable" notice instead of a full-price gift line. The notice is
 *    mirrored to localStorage so it survives the /cart page reload that clearCode()
 *    triggers (read-once, 2-minute TTL).
 *
 * Impact specifics (vs Dawn):
 *  - No window.routes global — routes come from the catalog blob.
 *  - Cart changes are observed via the theme's `cart:change` document event
 *    (detail: {cart, baseEvent}); our own raw fetch calls do NOT fire it.
 *  - After our own cart mutations we dispatch `cart:refresh` on documentElement —
 *    Impact re-renders all cart UI itself (replaces Dawn's pubsub + section
 *    rendering; see Maestrooo technical documentation).
 *  - Impact swaps cart regions via innerHTML — hydration via MutationObserver,
 *    guarded writes only (see setText below).
 *
 * Loading: site-wide via layout/theme.liquid (deferred) — the add/remove logic must
 * also run on non-cart pages (PDP add-to-cart can cross the threshold); the visible
 * widget is rendered by snippets/mampe-cart-gift.liquid in the cart drawer / cart page.
 */
(function () {
  'use strict';

  // The snippet can be rendered twice (drawer + cart page); the script tag itself
  // is only executed on first paint — innerHTML swaps never execute scripts.
  if (window.mampeCartGiftInit) return;
  window.mampeCartGiftInit = true;

  var LS_KEY = 'mampe:cart-gift';
  var LS_NOTICE_KEY = 'mampe:cart-gift-notice';
  var NOTICE_TTL = 120000; // conflict notice survives reloads for 2 minutes max
  var GIFT_PROP = '_gift';

  var SELECTORS = {
    catalog: '[data-mampe-gift-catalog]',
    root: '[data-mampe-gift-root]',
    form: '[data-mampe-gift-form]',
    input: '[data-mampe-gift-input]',
    status: '[data-mampe-gift-status]',
    chip: '[data-mampe-gift-chip]',
    chipCode: '[data-mampe-gift-chip-code]',
    chipClear: '[data-mampe-gift-clear]',
    lineRemove: '[data-mampe-gift-line-remove]',
  };

  var state = {
    code: null, // active code (persisted)
    entry: null, // catalog entry for the code (null = regular code)
    declined: false, // user removed the gift on purpose — no auto re-add
    busy: false, // mutation in flight
    selfRemoved: false, // we removed the line ourselves (not the user)
    cart: null, // last cart snapshot
    draft: '', // typed, not yet submitted (survives drawer re-renders)
    sessionSetAt: 0, // last applyCode — server mirror must not race the /discount/ fetch
    notice: null, // one-shot status notice without an active code ('conflict')
  };

  var fmtEUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  var fmtDate = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  function config() {
    var el = document.querySelector(SELECTORS.catalog);
    var fallback = {
      routes: { cartUrl: '/cart', cartAddUrl: '/cart/add', cartChangeUrl: '/cart/change' },
      gifts: [],
    };
    if (!el) return fallback;
    try {
      var parsed = JSON.parse(el.textContent);
      return {
        routes: (parsed && parsed.routes) || fallback.routes,
        gifts: (parsed && parsed.gifts) || [],
      };
    } catch (err) {
      return fallback;
    }
  }

  function routes() {
    return config().routes;
  }

  // ---------------------------------------------------------------- catalog / state

  function findEntry(code) {
    var gifts = config().gifts;
    for (var i = 0; i < gifts.length; i++) {
      if (gifts[i].code === code) return gifts[i];
    }
    return null;
  }

  function windowState(entry, now) {
    if (!entry) return 'open'; // regular codes: Shopify decides at checkout
    if (entry.startsAt && now < new Date(entry.startsAt).getTime()) return 'not-started';
    if (entry.endsAt && now > new Date(entry.endsAt).getTime()) return 'expired';
    return 'open';
  }

  function persist() {
    try {
      if (state.code) localStorage.setItem(LS_KEY, JSON.stringify({ code: state.code }));
      else localStorage.removeItem(LS_KEY);
    } catch (err) {
      /* private mode etc. — session-only then */
    }
  }

  function storeNotice() {
    // Mirror the conflict notice so it survives the /cart reload clearCode() fires.
    try {
      localStorage.setItem(LS_NOTICE_KEY, JSON.stringify({ type: 'conflict', at: Date.now() }));
    } catch (err) {
      /* in-memory notice still covers the drawer */
    }
  }

  function clearNotice() {
    state.notice = null;
    try {
      localStorage.removeItem(LS_NOTICE_KEY);
    } catch (err) { /* ignore */ }
  }

  // ---------------------------------------------------------------- cart helpers

  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }

  function giftLine(cart, code) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftLine(items[i]) && (!code || items[i].properties[GIFT_PROP] === code)) return items[i];
    }
    return null;
  }

  // Threshold base excludes the gift line itself — stricter than Shopify (which checks
  // the subtotal incl. gift position), so the server never rejects what we accepted.
  function baseTotal(cart) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (!isGiftLine(items[i])) total += items[i].final_line_price;
    }
    return total;
  }

  // ---------------------------------------------------------------- status UI

  // Status copy lives on the widget root as data-mcg-tpl-* attributes (rendered by
  // mampe-cart-gift.liquid) — German literals below are last-resort fallbacks only.
  function tpl(root, name, fallback) {
    var v = root ? root.getAttribute('data-mcg-tpl-' + name) : null;
    return v != null && v !== '' ? v : fallback;
  }

  function fill(text, vars) {
    return String(text).replace(/\{(\w+)\}/g, function (m, key) {
      return vars[key] != null ? vars[key] : m;
    });
  }

  function statusText(root) {
    if (!state.code && state.notice === 'conflict') {
      return tpl(root, 'conflict',
        'Der reduzierte Betrag aus deinem Konfigurator zählt leider nicht zum Mindestbestellwert dieses Codes.');
    }
    if (!state.code) return '';
    if (!state.entry) return tpl(root, 'applied', 'Code übernommen. Er wird beim Checkout geprüft.');
    var wState = windowState(state.entry, Date.now());
    if (wState === 'not-started') {
      return fill(tpl(root, 'not-started', 'Dieser Code ist erst ab {date} gültig.'), {
        date: fmtDate.format(new Date(state.entry.startsAt)),
      });
    }
    if (wState === 'expired') return tpl(root, 'expired', 'Diese Aktion ist leider beendet.');
    var base = baseTotal(state.cart);
    var title = state.entry.title;
    if (giftLine(state.cart, state.code)) {
      return fill(tpl(root, 'in-cart', 'Geschafft — dein Geschenk ({title}) liegt im Warenkorb.'), { title: title });
    }
    if (state.declined) return tpl(root, 'removed', 'Dein Geschenk wurde entfernt. Gib den Code erneut ein, um es wieder zu erhalten.');
    var missing = state.entry.threshold - base;
    if (missing > 0) {
      return fill(tpl(root, 'missing', 'Nur noch {amount} bis zu deinem Geschenk: {title}'), {
        amount: fmtEUR.format(missing / 100),
        title: title,
      });
    }
    return fill(tpl(root, 'waiting', 'Dein Geschenk ({title}) wartet auf dich.'), { title: title });
  }

  function setText(el, text) {
    // Only touch the DOM when the value actually changes — writing the SAME string
    // still replaces the text node, which feeds the MutationObserver below and
    // would create an endless hydrate loop (Aseli cart page froze, 2026-08-24).
    if (el && el.textContent !== text) el.textContent = text;
  }

  function syncWidget() {
    var roots = document.querySelectorAll(SELECTORS.root);
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var input = root.querySelector(SELECTORS.input);
      // never fight the customer's cursor while they are typing
      if (input && document.activeElement !== input) input.value = state.draft || state.code || '';
      var status = root.querySelector(SELECTORS.status);
      setText(status, statusText(root));
      var chip = root.querySelector(SELECTORS.chip);
      if (chip) {
        var show = !!state.code;
        if (chip.hidden === show) chip.hidden = !show; // only write on change
        setText(chip.querySelector(SELECTORS.chipCode), state.code || '');
      }
    }
  }

  function hydrateRoots() {
    // Mark + fill widget nodes that Impact's section swaps newly injected.
    var roots = document.querySelectorAll('[data-mampe-gift-root]:not([data-hydrated])');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) roots[i].setAttribute('data-hydrated', '1');
    syncWidget();
  }

  // ---------------------------------------------------------------- impact refresh

  function refreshTheme() {
    // Maestrooo-documented way to make Impact re-render its cart UI (count, drawer)
    // after mutations it did not perform itself. UI-only — no drawer opens.
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    // cart:refresh does NOT re-render the cart PAGE's main section — Impact itself
    // page-reloads there after its own mutations (pageType check in its quantity
    // logic), so we follow the same idiom. The chip/code survive via localStorage;
    // initCart re-hydrates widget + gift line after the reload (bug 2026-09-10:
    // the gift was in the cart server-side but /cart kept showing the stale view).
    if (window.themeVariables && window.themeVariables.settings
        && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    }
  }

  // ---------------------------------------------------------------- gift mutations

  function setSessionDiscount(code) {
    // /discount/<CODE> sets the checkout discount session cookie — the customer never
    // re-enters the code at checkout. Always "succeeds"; Shopify validates at checkout.
    // RETURNS a promise: the BxGY line discount is computed per-request from this
    // session, so callers must await it before mutating/rendering the cart — otherwise
    // the gift line renders at full price until the next cart event (bug 2026-09-10).
    // The redirect chain (302 -> full page) is slow; the verification poll in
    // verifyGiftDiscount() covers any residue.
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .catch(function () {});
  }

  function clearSessionDiscount() {
    // The /discount/<CODE> visit cannot be "un-done": Shopify keeps the code in the
    // cart session even after the widget clears its own state (Aseli bug 2026-09-03).
    // /cart/update.js removes it server-side. RETURNS a promise — clearCode() must
    // await it (no parallel cart mutations: racing update.js + change.js produced a
    // broken checkout session, bug 2026-09-10).
    // Cookie deletion is back in (2026-09-10, 3rd round): cart.js reads stay clean
    // without it, but the CHECKOUT may pre-fill the code from the discount_code
    // cookie independently — with the gift line gone that dead-ends the checkout
    // (generic error). Deleting costs nothing; the no-resurrection finding for
    // update.js stands either way.
    var clearing = fetch(routes().cartUrl + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
      credentials: 'same-origin',
    }).catch(function () {});
    document.cookie = 'discount_code=; Path=/; Max-Age=0; SameSite=Lax';
    return clearing;
  }

  function addGift() {
    if (state.busy || !state.entry || !state.code) return Promise.resolve();
    if (giftLine(state.cart, state.code)) return Promise.resolve();
    state.busy = true;
    return fetch(routes().cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(state.entry.variantId), quantity: 1, properties: privProps() }],
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
        refreshTheme();
        verifyGiftDiscount();
      })
      .catch(function () {})
      .finally(function () {
        state.busy = false;
        syncWidget();
      });
  }

  function privProps() {
    var props = {};
    props[GIFT_PROP] = state.code;
    return props;
  }

  // raw mutation without busy-flag handling — used by removeGift() and the
  // sequential clearCode() chain (which manages state.busy itself)
  function mutateRemoveGift(line) {
    return fetch(routes().cartChangeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: line.key,
        quantity: 0,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
      })
      .catch(function () {});
  }

  function removeGift(line) {
    if (state.busy || !line) return Promise.resolve();
    state.busy = true;
    state.selfRemoved = true;
    return mutateRemoveGift(line)
      .then(function () {
        refreshTheme();
      })
      .finally(function () {
        state.busy = false;
        state.selfRemoved = false;
        syncWidget();
      });
  }

  // ---------------------------------------------------------------- decisions

  // The server's verdict on the session code: listed, but applicable === false means
  // Shopify refuses to APPLY it to this cart. Today's cause: an active product
  // discount (Konfirabatt on configurator products) — all products in a BxGY's buy
  // set are ineligible for further product discounts, and ours buys from the
  // ALL-products collection, so the BxGY yields ("best value overall"). Strict
  // check: a code MISSING from discount_codes (cookie not settled yet) must not
  // trigger the guard — only a positive applicable:false statement does.
  function codeBlockedByServer(cart) {
    if (!state.code || !cart || !Array.isArray(cart.discount_codes)) return false;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var d = cart.discount_codes[i];
      if (d && d.code === state.code && d.applicable === false) return true;
    }
    return false;
  }

  // Conflict guard (2026-09-16): code active, threshold + window fine, but the
  // server won't apply it (codeBlockedByServer). Without the guard the gift line
  // sat in the cart at FULL price (live observed with halb60 vs. Konfirabatt).
  // Instead: explain, remove the line, clear the session — via the proven
  // sequential clearCode() chain. clearCode() is busy-guarded; callers re-check
  // (verifyGiftDiscount retries, evaluate re-runs on every cart event).
  function blockedGuard() {
    if (!state.code) return;
    state.notice = 'conflict';
    storeNotice();
    clearCode();
  }

  function evaluate(prevCart) {
    if (!state.cart) return;
    var line = giftLine(state.cart);

    if (!state.code || !state.entry) {
      // no active gift code — any leftover gift line belongs to an older code
      if (line) removeGift(line);
      return;
    }

    // outside the campaign window the server no longer discounts the gift — a
    // leftover line would ring up at full price, so it has to go
    if (windowState(state.entry, Date.now()) !== 'open') {
      if (line) removeGift(line);
      return;
    }

    var base = baseTotal(state.cart);
    if (base < state.entry.threshold) {
      state.declined = false;
      if (line) removeGift(line);
      return;
    }

    // threshold met
    if (line && line.properties[GIFT_PROP] !== state.code) {
      removeGift(line); // stale line from a previous code
      return;
    }
    // Server rejects the code although threshold + window are fine — another
    // discount (Konfigurator) blocks the BxGY. No full-price gift line: clean up
    // and explain. Runs for line-present AND not-yet-added cases (skips addGift).
    if (codeBlockedByServer(state.cart)) {
      blockedGuard();
      return;
    }
    if (!line) {
      // user-removed detection: line was there in the previous snapshot, gone now,
      // threshold still met, and we did not remove it ourselves -> respect the choice
      var wasThere = prevCart && giftLine(prevCart);
      if (wasThere && !state.selfRemoved) {
        // The user removed the gift line DIRECTLY. An active code session without
        // its gift line breaks the Shopify checkout (BxGY get-item missing ->
        // generic error that sticks for the cart token, bug 2026-09-10) — so the
        // code goes with the bottle: chip + server session + cookie, sequentially.
        // Re-entering the code brings the gift back. (If state.busy blocks the
        // clear, the chip stays and the working manual clear path remains.)
        state.declined = true;
        clearCode();
        return;
      }
      if (!state.declined && !state.busy) addGift();
    }
  }

  function applyCode(raw) {
    var code = String(raw || '').trim().toLowerCase();
    if (!code) return;

    clearNotice(); // a fresh code entry retires the conflict explanation
    state.sessionSetAt = Date.now();
    var entry = findEntry(code);
    var previousEntry = state.entry;
    state.code = code;
    state.entry = entry;
    state.declined = false;
    state.draft = code;
    persist();
    syncWidget(); // 1) instant feedback — status + chip show before any network call

    // 2) set the checkout session FIRST and AWAIT it — the BxGY line discount is
    //    computed per-request from this session; deciding/adding/rendering before
    //    the cookie settled showed the gift at full price (bug 2026-09-10).
    var sessionReady = (!entry || windowState(entry, Date.now()) === 'open')
      ? setSessionDiscount(code)
      : Promise.resolve();

    // 3) decide on a FRESH cart snapshot — state.cart may be missing (early entry)
    //    or stale; a wrong "not over threshold" verdict here was the bug that made
    //    code entry look dead until the next cart event (Aseli 2026-08-24).
    sessionReady.then(function () {
      withFreshCart(function () {
        var line = giftLine(state.cart);
        // switching codes (or moving to a regular code) — the old gift line must go:
        // the checkout only takes one code, so the line would be charged at full price
        var codeChanged = previousEntry && (!entry || entry.code !== previousEntry.code);
        if (line && codeChanged) {
          removeGift(line).then(function () {
            maybeAddGift();
            verifyGiftDiscount();
            syncWidget();
          });
          return;
        }
        maybeAddGift();
        verifyGiftDiscount();
        syncWidget();
      });
    });
  }

  function maybeAddGift() {
    if (!state.entry || windowState(state.entry, Date.now()) !== 'open') return;
    if (state.entry && state.cart && baseTotal(state.cart) >= state.entry.threshold) addGift();
  }

  function giftLineDiscounted(cart) {
    var line = cart && giftLine(cart, state.code);
    return !!(line && line.line_level_discount_allocations && line.line_level_discount_allocations.length);
  }

  // Self-healing for the full-price render race (bug 2026-09-10): the BxGY line
  // discount is computed server-side PER REQUEST from the code session. If the
  // gift line exists but carries no line-level discount allocation yet, the
  // session cookie settled after our last render — re-read and re-render until
  // it shows up (bounded poll, stops when discounted / line gone / code gone).
  function verifyGiftDiscount(attempt) {
    attempt = attempt || 0;
    if (attempt >= 5 || !state.code || !state.entry) return;
    if (windowState(state.entry, Date.now()) !== 'open') return;
    if (!state.cart || !giftLine(state.cart, state.code)) return;
    if (codeBlockedByServer(state.cart)) {
      blockedGuard();
      // clearCode() was busy-blocked (still code active) -> re-check next attempt
      if (state.code) verifyGiftDiscount(attempt + 1);
      return;
    }
    if (giftLineDiscounted(state.cart)) return;
    setTimeout(function () {
      withFreshCart(function () {
        if (!giftLine(state.cart, state.code)) return; // line gone meanwhile
        if (codeBlockedByServer(state.cart)) {
          blockedGuard();
          if (state.code) verifyGiftDiscount(attempt + 1);
          return;
        }
        if (giftLineDiscounted(state.cart)) {
          refreshTheme(); // session settled — theme re-render shows 0 EUR + correct total
          return;
        }
        verifyGiftDiscount(attempt + 1);
      });
    }, 400);
  }

  function withFreshCart(fn) {
    fetch(routes().cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        fn();
      })
      .catch(function () {
        /* keep the last snapshot; decisions re-run on the next cart event */
      });
  }

  // Strictly SEQUENTIAL — bug 2026-09-10: the old fire-and-forget version raced
  // update.js (session clear) against change.js (line removal); a checkout started
  // during the race hit a half-cleared cart session and died with Shopify's generic
  // "data not correct, go back" error, while an aborted line removal left the gift
  // in the cart. Chain: 1) remove gift line (code still active -> line is 0 EUR
  // until it disappears, never a full-price gift without code), 2) clear the
  // server-side session, 3) ONE theme refresh. state.busy guards the whole chain.
  function clearCode() {
    if (state.busy) return;
    state.busy = true;
    state.code = null;
    state.entry = null;
    state.declined = false;
    state.draft = '';
    persist();
    syncWidget();
    withFreshCart(function () {
      var line = giftLine(state.cart);
      var chain = line ? mutateRemoveGift(line) : Promise.resolve();
      chain
        .then(function () {
          return clearSessionDiscount();
        })
        .then(function () {
          refreshTheme();
        })
        .catch(function () { /* steps already swallow errors; keep the chain moving */ })
        .finally(function () {
          state.busy = false;
          syncWidget();
        });
    });
  }

  // ---------------------------------------------------------------- server mirror

  function serverHasCode(cart) {
    // Cart responses expose the live session codes — invalid and not-yet-applicable
    // ones included (flagged applicable:false, Aseli 2026-09-03), so a code that
    // is missing entirely was removed server-side. Unknown payload shape → keep state.
    if (!state.code || !cart || !Array.isArray(cart.discount_codes)) return true;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      if (cart.discount_codes[i] && cart.discount_codes[i].code === state.code) return true;
    }
    return false;
  }

  function syncFromServer(cart) {
    // Checkout is Shopify territory: removing a code there never notifies the theme,
    // so the chip would resurrect a code the checkout had dropped (Aseli follow-up
    // bug 2026-09-03). Mirror the server session back into the widget on every cart
    // contact. Grace period: right after applyCode the /discount/ fetch may not have
    // settled — the code would be missing from discount_codes for a moment.
    // Codes outside their campaign window are never sent as a session, so the mirror
    // must ignore them too.
    if (!state.code) return;
    if (state.entry && windowState(state.entry, Date.now()) !== 'open') return;
    if (Date.now() - (state.sessionSetAt || 0) < 10000) return;
    if (!serverHasCode(cart)) clearCode();
  }

  // ---------------------------------------------------------------- events / init

  document.addEventListener('submit', function (event) {
    var form = event.target.closest(SELECTORS.form);
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector(SELECTORS.input);
    if (input) applyCode(input.value);
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest(SELECTORS.input);
    if (input) state.draft = input.value;
  });

  document.addEventListener('click', function (event) {
    var chipClear = event.target.closest(SELECTORS.chipClear);
    if (chipClear) {
      clearCode();
      return;
    }
    // Gift-line remove BUTTON (rendered instead of a link — Impact's AJAX interception
    // binds to <line-item-quantity>, which gift lines don't have; a plain link would
    // navigate and kill this clear mid-flight, bug 2026-09-10). Same sequential chain
    // as the chip: line out -> session+cookie out -> ONE refresh.
    var lineRemove = event.target.closest(SELECTORS.lineRemove);
    if (lineRemove) {
      event.preventDefault();
      clearCode();
    }
  });

  function onCartChange(event) {
    // Impact's catch-all cart event (detail: {cart, baseEvent}) — fires on theme
    // originated additions/changes/removals, NOT on our own raw fetch calls.
    var detail = (event && event.detail) || {};
    var prev = state.cart;
    if (detail.cart) state.cart = detail.cart;
    if (!detail.cart) {
      syncWidget();
      return;
    }
    syncFromServer(state.cart);
    evaluate(prev);
    syncWidget();
  }

  function initCart() {
    return fetch(routes().cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        if (state.code) state.entry = findEntry(state.code);
        syncFromServer(cart);
        evaluate(null);
        syncWidget();
      })
      .catch(function () {});
  }

  function init() {
    document.addEventListener('cart:change', onCartChange);

    // Impact swaps cart regions via innerHTML on every change — hydrate NEW widget
    // nodes only. The observer must never react to our own writes, so all writes
    // go through value-guards (setText / hidden-on-change / attribute marking):
    // a re-invocation finds no unhydrated roots and terminates. rAF-batched.
    var hydratePending = false;
    var observer = new MutationObserver(function () {
      if (hydratePending) return;
      hydratePending = true;
      requestAnimationFrame(function () {
        hydratePending = false;
        hydrateRoots();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    var savedCode = null;
    try {
      savedCode = (JSON.parse(localStorage.getItem(LS_KEY) || 'null') || {}).code;
    } catch (err) {
      savedCode = null;
    }
    if (savedCode) {
      state.code = savedCode;
      state.entry = findEntry(savedCode);
    }
    // One-shot conflict notice across the /cart reload clearCode() triggers:
    // read, show (within TTL), remove — never resurrected on later visits.
    try {
      var savedNotice = JSON.parse(localStorage.getItem(LS_NOTICE_KEY) || 'null');
      if (savedNotice && savedNotice.type === 'conflict'
          && Date.now() - (savedNotice.at || 0) < NOTICE_TTL) {
        state.notice = 'conflict';
      }
      localStorage.removeItem(LS_NOTICE_KEY);
    } catch (err) { /* ignore */ }
    initCart();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
